import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AlertCircle,
  BarChart3,
  Bot,
  Brain,
  Building2,
  Calculator,
  CheckCircle2,
  FileText,
  Landmark,
  MessageCircle,
  Plus,
  Save,
  Scale,
  ShieldCheck,
  Table2,
  Trash2,
  Truck,
  Users,
} from 'lucide-react';

import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectItem } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { getUserRole, isOwnerAccount } from '../lib/access';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';

const initialForm = {
  organizationName: '',
  organizationEmail: '',
  organizationAddress: '',
  name: '',
  phone: '',
  email: '',
  password: '',
  role: 'Super Admin',
};

const api = (path) => `${String(configData.API_SERVER || '').replace(/\/+$/, '')}${path}`;
const normalizeCode = (value) => (value || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
const getAgentCode = (agent) => (agent?.code || agent?.agent_code || '').trim();
const iconOptions = [
  { value: 'bot', label: 'Bot', icon: Bot },
  { value: 'chat', label: 'Chat', icon: MessageCircle },
  { value: 'doc', label: 'Document', icon: FileText },
  { value: 'sheet', label: 'Sheet', icon: Table2 },
  { value: 'tax', label: 'Tax', icon: Calculator },
  { value: 'law', label: 'Law', icon: Scale },
  { value: 'ship', label: 'Shipping', icon: Truck },
  { value: 'shield', label: 'Compliance', icon: ShieldCheck },
  { value: 'bank', label: 'Finance', icon: Landmark },
  { value: 'chart', label: 'Analytics', icon: BarChart3 },
  { value: 'people', label: 'People', icon: Users },
  { value: 'brain', label: 'AI', icon: Brain },
];
const getIconOption = (value) => iconOptions.find((option) => option.value === value) || iconOptions[0];

export function SubscriptionManagementPage() {
  const account = useSelector((state) => state.account || {});
  const owner = isOwnerAccount(account);
  const accountRole = getUserRole(account);

  const [form, setForm] = useState(initialForm);
  const [agents, setAgents] = useState([]);
  const [checkedAgents, setCheckedAgents] = useState([]);
  const [agentSeats, setAgentSeats] = useState({});
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentCode, setNewAgentCode] = useState('');
  const [newAgentDescription, setNewAgentDescription] = useState('');
  const [newAgentIcon, setNewAgentIcon] = useState('bot');
  const [newAgentFrontendCode, setNewAgentFrontendCode] = useState('');
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingCode, setDeletingCode] = useState('');
  const [loadingSubscriber, setLoadingSubscriber] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const totalSeats = useMemo(
    () => checkedAgents.reduce((sum, code) => sum + (Number(agentSeats[code]) || 1), 0),
    [agentSeats, checkedAgents]
  );

  const fetchAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await axiosInstance.get(api('/organization/agents/list/'));
      setAgents(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setMessageType('error');
      setMessage('Failed to load agents. Please refresh.');
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    if (owner) fetchAgents();
  }, [owner]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleAgent = (code) => {
    const alreadySelected = checkedAgents.includes(code);
    setCheckedAgents((prev) => alreadySelected ? prev.filter((item) => item !== code) : [...prev, code]);
    setAgentSeats((prev) => {
      const next = { ...prev };
      if (alreadySelected) {
        delete next[code];
      } else {
        next[code] = next[code] || 1;
      }
      return next;
    });
  };

  const handleSeatsChange = (code, value) => {
    setAgentSeats((prev) => ({ ...prev, [code]: Math.max(1, Number(value) || 1) }));
  };

  const fetchSubscriberForUpdate = async () => {
    const email = form.email.trim();
    if (!email) {
      setMessageType('error');
      setMessage('Enter the first user email before loading subscriber data.');
      return;
    }

    setLoadingSubscriber(true);
    setMessage('');
    try {
      const res = await axiosInstance.get(api('/organization/getSubscriberAccount'), { params: { email } });
      const data = res.data?.data || {};
      setForm({
        organizationName: data.organizationName || '',
        organizationEmail: data.organizationEmail || '',
        organizationAddress: data.organizationAddress || '',
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || email,
        password: '',
        role: data.role || 'Super Admin',
      });

      const selectedAgents = [];
      const selectedSeats = {};
      (data.allocations || []).forEach((item) => {
        if (item.agent_code) {
          selectedAgents.push(item.agent_code);
          selectedSeats[item.agent_code] = item.seats || 1;
        }
      });
      setCheckedAgents(selectedAgents);
      setAgentSeats(selectedSeats);
      setMessageType('info');
      setMessage('Subscriber loaded. Adjust details and save to update.');
    } catch (error) {
      setMessageType('error');
      setMessage(error.response?.data?.message || 'Subscriber not found.');
    } finally {
      setLoadingSubscriber(false);
    }
  };

  const handleCreateAgent = async () => {
    const name = newAgentName.trim();
    if (!name) {
      setMessageType('error');
      setMessage('Agent name is required.');
      return;
    }

    const code = newAgentCode.trim() ? normalizeCode(newAgentCode) : normalizeCode(name);
    const description = newAgentDescription.trim();
    const icon = newAgentIcon.trim();
    const frontend_code = newAgentFrontendCode.trim();
    setCreating(true);
    setMessage('');
    try {
      await axiosInstance.post(api('/organization/agents/create/'), { name, code, description, icon, frontend_code });
      setNewAgentName('');
      setNewAgentCode('');
      setNewAgentDescription('');
      setNewAgentIcon('bot');
      setNewAgentFrontendCode('');
      setShowAgentForm(false);
      setMessageType('success');
      setMessage('Agent created successfully.');
      await fetchAgents();
    } catch (error) {
      setMessageType('error');
      setMessage(error.response?.data?.message || 'Failed to create agent.');
    } finally {
      setCreating(false);
    }
  };

  const confirmDeleteAgent = async () => {
    const code = getAgentCode(agentToDelete);
    if (!code) {
      setMessageType('error');
      setMessage('Agent code missing. Cannot delete this agent.');
      setAgentToDelete(null);
      return;
    }

    setDeletingCode(code);
    setMessage('');
    try {
      const res = await axiosInstance.post(api('/organization/agents/delete/'), { code });
      setCheckedAgents((prev) => prev.filter((item) => item !== code));
      setAgentSeats((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setMessageType('success');
      setMessage(res.data?.message || `Agent "${agentToDelete?.name || code}" deleted successfully.`);
      await fetchAgents();
    } catch (error) {
      setMessageType('error');
      setMessage(error.response?.data?.message || error.response?.data?.detail || 'Failed to delete agent.');
    } finally {
      setDeletingCode('');
      setAgentToDelete(null);
    }
  };

  const validate = () => {
    const requiredFields = [
      ['organizationName', 'Organization name is required.'],
      ['organizationEmail', 'Organization email is required.'],
      ['organizationAddress', 'Organization address is required.'],
      ['name', 'First user name is required.'],
      ['phone', 'First user phone is required.'],
      ['email', 'First user email is required.'],
      ['password', 'Password is required.'],
      ['role', 'First user role is required.'],
    ];

    const missing = requiredFields.find(([field]) => !String(form[field] || '').trim());
    if (missing) return missing[1];
    if (!checkedAgents.length) return 'Please select at least one agent.';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationMessage = validate();
    if (validationMessage) {
      setMessageType('error');
      setMessage(validationMessage);
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const selectedSeatTotal = checkedAgents.reduce((total, code) => total + (Number(agentSeats[code]) || 1), 0);
      const payload = {
        update_if_exists: true,
        organizationName: form.organizationName.trim(),
        organizationEmail: form.organizationEmail.trim(),
        organizationAddress: form.organizationAddress.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password.trim(),
        role: form.role,
        licensesAssigned: selectedSeatTotal,
        allocations: checkedAgents.map((code) => ({
          agent_code: code,
          seats: Number(agentSeats[code]) || 1,
        })),
      };

      const res = await axiosInstance.post(api('/organization/addSubscribersAccount'), payload);
      setMessageType('success');
      setMessage(res.data?.message || 'Organization account created/updated successfully.');
      setForm(initialForm);
      setCheckedAgents([]);
      setAgentSeats({});
    } catch (error) {
      setMessageType('error');
      setMessage(error.response?.data?.message || error.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  if (!owner && accountRole !== 'Super Admin') {
    return (
      <div className="p-10">
        <Card className="p-10 text-center">
          <h2 className="font-display text-2xl font-semibold">Owner access required</h2>
          <p className="mt-2 text-sm text-muted-foreground">Only the Owner can manage subscriptions and agent creation.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Administration"
        title="Manage Subscription"
        description="Create subscriber organizations, assign the first user, and manage agent seat pools."
        actions={
          <span className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {accountRole || 'Owner'}
          </span>
        }
      />

      <div className="px-6 lg:px-10 py-6 space-y-6">
        {message && (
          <Card className={`flex items-start gap-3 p-4 text-sm ${
            messageType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : messageType === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'text-muted-foreground'
          }`}
          >
            {messageType === 'success' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : null}
            {messageType === 'error' ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> : null}
            {message}
          </Card>
        )}

        <form onSubmit={handleSubmit} className="grid gap-6">
          <Card className="p-5">
            <SectionTitle icon={Building2} title="Organization" subtitle="Subscriber company details" />
            <div className="mt-5 grid gap-5 lg:grid-cols-3">
              <Field label="Organization Name" required>
                <Input value={form.organizationName} onChange={(event) => updateField('organizationName', event.target.value)} placeholder="Enter organization name" />
              </Field>
              <Field label="Organization Email" required>
                <Input type="email" value={form.organizationEmail} onChange={(event) => updateField('organizationEmail', event.target.value)} placeholder="Enter organization email" />
              </Field>
              <Field label="Organization Address" required>
                <Input value={form.organizationAddress} onChange={(event) => updateField('organizationAddress', event.target.value)} placeholder="Enter organization address" />
              </Field>
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle icon={Users} title="First User" subtitle="This user receives Manage User access based on the selected admin role" />
            <div className="mt-5 grid gap-5 lg:grid-cols-3">
              <Field label="First User Name" required>
                <Input value={form.name} onChange={(event) => updateField('name', event.target.value)} placeholder="Enter first user name" />
              </Field>
              <Field label="First User Phone" required>
                <Input value={form.phone} onChange={(event) => updateField('phone', event.target.value)} placeholder="Enter first user phone" />
              </Field>
              <Field label="First User Email" required>
                <div className="flex gap-2">
                  <Input type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} placeholder="Enter first user email" />
                  <Button type="button" variant="outline" onClick={fetchSubscriberForUpdate} disabled={loadingSubscriber || saving}>
                    {loadingSubscriber ? 'Loading...' : 'Load'}
                  </Button>
                </div>
              </Field>
              <Field label="First User Role" required>
                <Select value={form.role} onValueChange={(value) => updateField('role', value)} className="h-9 w-full rounded-md border-input bg-transparent py-1 shadow-sm">
                  <SelectItem value="Super Admin">Super Admin</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Department Head">Department Head</SelectItem>
                </Select>
              </Field>
              <Field label="Password" required>
                <Input type="password" value={form.password} onChange={(event) => updateField('password', event.target.value)} placeholder="Enter password" autoComplete="new-password" />
              </Field>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <SectionTitle icon={Plus} title="Agent Seat Pools" subtitle="Selected agents become available in Manage User for assignment" />
              <div className="flex items-center gap-2">
                {checkedAgents.length > 0 && (
                  <span className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground">
                    {checkedAgents.length} agents / {totalSeats} seats
                  </span>
                )}
                <Button type="button" variant="outline" onClick={() => setShowAgentForm((value) => !value)}>
                  <Plus className="h-4 w-4" />
                  Add New Agent
                </Button>
              </div>
            </div>

            {showAgentForm && (
              <div className="mt-5 grid gap-4 rounded-xl border border-border/70 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Agent Name" required>
                    <Input value={newAgentName} onChange={(event) => setNewAgentName(event.target.value)} placeholder="Example: HR Agent" />
                  </Field>
                  <Field label="Agent Code">
                    <Input value={newAgentCode} onChange={(event) => setNewAgentCode(event.target.value)} placeholder="Optional, example: hr_agent" />
                  </Field>
                </div>
                <Field label="Bot Icon">
                  <div className="grid grid-cols-6 gap-2 sm:grid-cols-12">
                    {iconOptions.map(({ value, label, icon: Icon }) => {
                      const selected = newAgentIcon === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          className={`grid h-10 place-items-center rounded-md border text-sm transition ${
                            selected
                              ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                              : 'border-border bg-card text-muted-foreground hover:border-primary/60 hover:text-foreground'
                          }`}
                          onClick={() => setNewAgentIcon(value)}
                          title={label}
                          aria-label={label}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Description">
                  <Textarea
                    value={newAgentDescription}
                    onChange={(event) => setNewAgentDescription(event.target.value)}
                    placeholder="Short description shown for this agent"
                    rows={3}
                  />
                </Field>
                <Field label="Frontend Code">
                  <Textarea
                    value={newAgentFrontendCode}
                    onChange={(event) => setNewAgentFrontendCode(event.target.value)}
                    placeholder={`Paste full React page code here. Example:\nimport React from "react";\n\nexport default function MyAgentPage() {\n  return <div>My custom agent UI</div>;\n}`}
                    rows={10}
                    className="font-mono text-xs"
                  />
                </Field>
                <div className="flex justify-end">
                <Button type="button" onClick={handleCreateAgent} disabled={creating || !newAgentName.trim()}>
                  {creating ? 'Creating...' : 'Create Agent'}
                </Button>
                </div>
              </div>
            )}

            <div className="mt-5 overflow-hidden rounded-xl border border-border/70">
              {loadingAgents ? (
                <p className="p-4 text-sm text-muted-foreground">Loading agents...</p>
              ) : agents.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No agents found. Click Add New Agent to create one.</p>
              ) : (
                agents.map((agent) => {
                  const code = getAgentCode(agent);
                  const selected = checkedAgents.includes(code);
                  const isDeleting = deletingCode === code;
                  return (
                    <div key={code || agent.id || agent.name} className={`grid gap-3 border-b border-border/60 p-3 last:border-b-0 md:grid-cols-[1fr_140px_auto] md:items-center ${selected ? 'bg-primary/5' : ''}`}>
                      <label className="flex cursor-pointer items-center gap-3">
                        <input type="checkbox" checked={selected} disabled={isDeleting || saving} onChange={() => toggleAgent(code)} />
                        {(() => {
                          const Icon = getIconOption(agent.icon).icon;
                          return (
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-border/70 bg-card text-sm font-semibold">
                              <Icon className="h-4 w-4" />
                        </span>
                          );
                        })()}
                        <span>
                          <span className="block text-sm font-medium text-foreground">{agent.name || code}</span>
                          <span className="block font-mono text-xs text-muted-foreground">{code}</span>
                          {agent.description && <span className="mt-1 block text-xs text-muted-foreground">{agent.description}</span>}
                        </span>
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={selected ? agentSeats[code] || 1 : ''}
                        disabled={!selected || isDeleting || saving}
                        onChange={(event) => handleSeatsChange(code, event.target.value)}
                        placeholder="Seats"
                      />
                      <Button type="button" variant="destructive" size="sm" onClick={() => setAgentToDelete(agent)} disabled={isDeleting || saving}>
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Create / Update Organization'}
            </Button>
          </div>
        </form>
      </div>

      {agentToDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4">
          <Card className="w-full max-w-md p-5">
            <h3 className="font-display text-lg font-semibold">Delete Agent</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Delete agent <strong>{agentToDelete.name || getAgentCode(agentToDelete)}</strong>? This removes the agent and related allocations.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAgentToDelete(null)} disabled={!!deletingCode}>Cancel</Button>
              <Button type="button" variant="destructive" onClick={confirmDeleteAgent} disabled={!!deletingCode}>
                {deletingCode ? 'Deleting...' : 'Yes, Delete'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {message && (
        <div className={`fixed bottom-5 right-5 z-40 flex max-w-sm items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${
          messageType === 'success'
            ? 'border-emerald-200 bg-white text-emerald-800'
            : messageType === 'error'
              ? 'border-red-200 bg-white text-red-700'
              : 'border-border bg-white text-foreground'
        }`}
        >
          {messageType === 'success' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : null}
          {messageType === 'error' ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> : null}
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/10">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
        {label}{required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}
